import React, { useRef, useState, useCallback, useEffect } from 'react';
import { ListBox } from 'primereact/listbox';
import { Button } from 'primereact/button';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { FileUpload } from 'primereact/fileupload';
import { MultiSelect } from 'primereact/multiselect';
import { uploadCsv } from '../../Services/Api';

export default function BusinessLines({
  form,
  handleChange,
  medicineCategories,
  levelOptions,
  diagnosticCategories,
  diagnosticLevelOptions,
}) {
  const toast = useRef(null);

  const [draftLine, setDraftLine] = useState('');
  const [draftLevel, setDraftLevel] = useState('');
  const [draftCategory, setDraftCategory] = useState([]);
  const [draftItems, setDraftItems] = useState([]);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);
  const MAX_FILE_SIZE = 5 * 1024 * 1024;

  const generateUniqueId = useCallback(
    () => `entry_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    []
  );

  const [entries, setEntries] = useState([]);

  useEffect(() => {
    const withIds = (form.businessLines?.lines || []).map(entry => ({
      id: entry.id ?? generateUniqueId(),
      ...entry
    }));
    setEntries(withIds);
  }, [form.businessLines?.lines, generateUniqueId]);

  const availableLines = ['medicines', 'diagnostics', 'Consultation', 'Membership'].filter(
    line => !entries.some(entry => entry.line === line)
  );


  const resetDrafts = () => {
    setDraftLine('');
    setDraftLevel('');
    setDraftCategory([]);
    setDraftItems([]);
    setUploadedFileName('');
    setUploadError('');
  };

  // Helper function to add entry to the table
  const addEntryToTable = useCallback((line, level, value, fileName = null) => {
    const currentValue = Array.isArray(value) ? [...value] : value;

    const alreadyExists = entries.some(entry =>
      entry.line === line &&
      entry.level === level &&
      JSON.stringify(entry.value?.sort()) === JSON.stringify(currentValue.sort())
    );

    if (alreadyExists) {
      toast.current?.show({
        severity: 'error',
        summary: 'Duplicate Entry',
        detail: `This combination already exists.`,
        life: 3000
      });
      return false;
    }

    const newEntry = {
      id: generateUniqueId(),
      line: line,
      level: level,
      value: JSON.parse(JSON.stringify(currentValue)),
      fileName: fileName || null // Store filename for CSV uploads
    };

    const updated = [...entries, newEntry];
    setEntries(updated);
    handleChange('businessLines.lines', updated);
    return true;
  }, [entries, generateUniqueId, handleChange]);

  const onAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (['consultation', 'membership'].includes(draftLine.toLowerCase())) {
      const alreadyExists = entries.some(entry => entry.line === draftLine);
      if (alreadyExists) {
        toast.current?.show({ severity: 'error', summary: 'Duplicate Entry', detail: `This line already exists.`, life: 3000 });
        return;
      }

      const newEntry = { id: generateUniqueId(), line: draftLine };
      const updated = [...entries, newEntry];
      setEntries(updated);
      handleChange('businessLines.lines', updated);
      resetDrafts();
      return;
    }

    const currentValue = draftLevel === 'category' ? [...draftCategory] : [...draftItems];

    if (addEntryToTable(draftLine, draftLevel, currentValue)) {
      resetDrafts();
    }
  };

  const onRemove = (targetId, e) => {
    e.preventDefault();
    const updated = entries.filter(entry => entry.id !== targetId);
    setEntries(updated);
    handleChange('businessLines.lines', updated);
  };

  const onCSVSelect = async (event) => {
    const file = event.files[0];
    console.log(file, "rftgyh")
    if (!file) return;

    // validate
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setUploadError('Please upload a .csv file.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadError('File size exceeds 5MB.');
      return;
    }

    setUploadError('');
    setUploading(true);

    try {
      const typeMap = {
        manufacturer: 'manufacturer',
        skus: 'skus',
        labs: 'labs',
        testcode: 'testcodes'
      };
      const uploadType = typeMap[draftLevel] || draftLevel;
      const { data } = await uploadCsv(uploadType, file);
      console.log(data, "fgh")

      const values = data.values || [];
      setDraftItems(values);
      setUploadedFileName(file.name);

      // Automatically add the entry after successful upload
      if (values.length > 0 && draftLine && draftLevel) {
        const success = addEntryToTable(draftLine, draftLevel, values, file.name);
        if (success) {
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: `CSV uploaded and entry added successfully!`,
            life: 3000
          });
          resetDrafts();
        }
      }

    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: 'error',
        summary: 'Upload Error',
        detail: 'Server parsing failed.',
        life: 3000
      });
    } finally {
      setUploading(false);
    }
  };

  const removeUploadedFile = () => {
    setUploadedFileName('');
    setDraftItems([]);
    setUploadError('');
  };

  const downloadSampleCSV = () => {
    // Define the CSV content based on draftLevel - each level has only its own column
    let csvContent = '';

    if (draftLevel === 'manufacturer') {
      csvContent = 'manufacturer\nSample Manufacturer\nAnother Manufacturer\nTest Manufacturer';
    } else if (draftLevel === 'skus') {
      csvContent = 'sku\nSKU001\nSKU002\nSKU003';
    } else if (draftLevel === 'labs') {
      csvContent = 'lab\nLab001\nLab002\nLab003';
    } else if (draftLevel === 'testcode') {
      csvContent = 'testCode\nTEST001\nTEST002\nTEST003';
    }

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'sample.csv');
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    URL.revokeObjectURL(url);
  };
  return (
    <>
      <Toast ref={toast} />
      <div className="bg-white p-6 mb-6">
        <h4 className="text-xl font-semibold mb-4">Business Lines</h4>

        <div className="flex flex-wrap items-center gap-4 mb-6">
          <ListBox
            value={draftLine}
            options={availableLines.map(line => ({ label: line.charAt(0).toUpperCase() + line.slice(1), value: line }))}
            onChange={e => {
              setDraftLine(e.value);
              setDraftLevel('');
              setDraftCategory([]);
              setDraftItems([]);
              setUploadedFileName('');
              setUploadError('');
            }}
            placeholder="Select Line"
            className="w-1/4 border border-gray-300 rounded p-2"
          />

          {draftLine && !['consultation', 'membership'].includes(draftLine.toLowerCase()) && (
            <ListBox
              value={draftLevel}
              options={(draftLine === 'medicines' ? levelOptions : diagnosticLevelOptions)
                .map(l => ({ label: l.charAt(0).toUpperCase() + l.slice(1), value: l.toLowerCase() }))}
              onChange={e => {
                setDraftLevel(e.value);
                setDraftCategory([]);
                setDraftItems([]);
                setUploadedFileName('');
                setUploadError('');
              }}
              placeholder="Select Level"
              className="w-1/4 border border-gray-300 rounded p-2"
            />
          )}

          {draftLevel === 'category' &&
            !['consultation', 'membership'].includes(draftLine.toLowerCase()) && (
              draftLine === 'medicines' ? (
                <MultiSelect
                  value={draftCategory}
                  options={['RX', 'OTC', 'NRX', 'GX'].map(c => ({ label: c, value: c }))}
                  onChange={e => setDraftCategory(e.value)}
                  placeholder="Select Category"
                  className="w-1/4 border border-gray-300 rounded p-2"
                />
              ) : (
                <ListBox
                  value={draftCategory}
                  options={diagnosticCategories.map(c => ({ label: c, value: c }))}
                  onChange={e => setDraftCategory(e.value)}
                  multiple
                  placeholder="Select Category"
                  className="w-1/4 border border-gray-300 rounded p-2"
                />
              )
            )}

          {['manufacturer', 'skus', 'labs', 'testcode'].includes(draftLevel) &&
            !['consultation', 'membership'].includes(draftLine.toLowerCase()) && (
              <>
                <FileUpload
                  name="csv"
                  accept=".csv"
                  mode='basic'
                  onSelect={onCSVSelect}
                  disabled={uploading}
                  chooseLabel={uploading ? 'Uploading…' : 'Upload CSV'}
                  className="p-button-sm"
                  auto={false}
                />

                {/* Sample CSV Download Link */}
                <button
                  onClick={downloadSampleCSV}
                  className="text-blue-600 hover:text-blue-800 underline text-sm ml-2"
                  type="button"
                >
                  sample.csv
                </button>

                {uploadedFileName && (
                  <div className="inline-flex items-center bg-gray-100 px-3 py-1 rounded-full">
                    <span className="text-gray-800 mr-2 truncate max-w-xs">{uploadedFileName}</span>
                    <button onClick={removeUploadedFile} className="text-gray-600 hover:text-gray-800 font-bold">×</button>
                  </div>
                )}
              </>
            )
          }


          {/* Only show Add button for non-CSV upload scenarios */}
          {!['manufacturer', 'skus', 'labs', 'testcode'].includes(draftLevel) && (
            <Button
              label="Add"
              icon="pi pi-plus"
              type="button"
              className="cursor-pointer bg-gradient-to-r from-green-500 to-emerald-600 hover:from-emerald-600 hover:to-green-500 text-white font-semibold px-5 py-2 rounded-full shadow-md hover:shadow-lg transition-all duration-300 ease-in-out"
              disabled={
                !draftLine ||
                (
                  !['consultation', 'membership'].includes(draftLine.toLowerCase()) &&
                  (
                    !draftLevel ||
                    (draftLevel === 'category' ? draftCategory.length === 0 : draftItems.length === 0)
                  )
                )
              }
              onClick={onAdd}
            />
          )}
        </div>

        {entries.length > 0 && (
          <DataTable value={entries} dataKey="id" className="shadow-md rounded-lg" responsiveLayout="scroll">
            <Column field="line" header="Line" body={rowData => rowData.line?.charAt(0).toUpperCase() + rowData.line?.slice(1)} />
            <Column field="level" header="Level" body={rowData => rowData.level ? rowData.level.charAt(0).toUpperCase() + rowData.level.slice(1) : '-'} />
            {/* <Column field="value" header="Value" body={rowData => {
              // If it's a CSV upload with filename, show filename instead of all data
              if (rowData.fileName && Array.isArray(rowData.value) && rowData.value.length > 5) {
                return `${rowData.fileName} (${rowData.value.length} items)`;
              }
              return Array.isArray(rowData.value) ? rowData.value.join(', ') : rowData.value ?? '-';
            }} /> */}

            <Column
              header="Value"
              body={rowData => {
                if (rowData.fileName) {
                  // show only the CSV filename (and count, if you want)
                  return `${rowData.fileName} (${rowData.value.length} items)`;
                }
                // otherwise fall back to whatever values you had before
                return Array.isArray(rowData.value)
                  ? rowData.value.join(', ')
                  : rowData.value ?? '-';
              }}
            />
            <Column
              header="Action"
              body={rowData => (
                <Button icon="pi pi-trash" onClick={e => onRemove(rowData.id, e)} className="p-button-rounded p-button-danger p-button-text" />
              )}
            />
          </DataTable>
        )}
      </div>
    </>
  );
}